import {
  ChatFeatureConfigSchema,
  type ChatFeatureConfig,
  type ChatFeatureConfigWrite
} from '@school-journey/shared-types'

import type { ScreenFeatureEntry } from './entry'

/**
 * Global `chat`.
 *
 * absentIsEmpty: the fleet-default GLOBAL_FEATURE_CONFIGS row has no `chat`
 * block, and 5 of 33 school rows have none either (read-only production query,
 * `SystemConfiguration`, 2026-10-02: chat on 28 of 33 school rows). Those
 * schools get `SYSTEM_FEATURE_NOT_FOUND`.
 *
 * Fallback, for a school with no block:
 * - `enabled: false`: mobile treats a missing block as chat off
 *   (`apps/mobile/src/context/hooks/chat/use-chat-permissions.ts:296`).
 * - `visibleForRoles`: required by the schema. The server's own default for a
 *   chat block missing it (`apps/backend/src/services/config/global-feature-defaults.ts:29`,
 *   used by the read backfill at `:88-90`).
 *
 * Wire and change are the WRITE shape (`ChatFeatureConfigWrite`, SCH-1288 KTD9):
 * `studentGradeLevels: null` clears the stored list back to "All students". The
 * server settles it and never stores `null`
 * (`apps/backend/src/services/config/global-features.ts:779`). After such a save
 * the cached block still holds the `null` the body carried, so `toValue` reads
 * it as no list.
 */
export const chat: ScreenFeatureEntry<
  'global',
  'chat',
  ChatFeatureConfigWrite,
  ChatFeatureConfig,
  Partial<ChatFeatureConfigWrite>
> = {
  resource: 'global',
  wireName: 'chat',
  schema: ChatFeatureConfigSchema,
  writeMode: 'replace',
  absentIsEmpty: true,
  fallback: {
    enabled: false,
    visibleForRoles: ['student', 'parent', 'faculty', 'admin']
  },
  // The server keeps the stored grade list when the key is missing
  // (`apps/backend/src/services/config/global-features.ts:778`), so a save
  // that did not touch the picker leaves it out (SCH-1288 KTD9 rule 2).
  sendOnlyWhenChanged: ['studentGradeLevels'],
  toValue: ({ studentGradeLevels, ...rest }) =>
    studentGradeLevels === null || studentGradeLevels === undefined ? rest : { ...rest, studentGradeLevels }
}
